import { SCOPE_TYPE } from '../../services/utils';

export interface License {
  licenseId: string;
  licenseKey: string;
  orderId: string;
  status: string;
  assignedCharger?: string;
  type: string;
  includes4G: boolean;
  duration: number;
  companyId: string;
  creationDate: Date;
  activationDate?: Date;
  expirationDate?: Date;
  ports?: number;
  orderNote?: string;
}

export interface LicenseSelfServeRenewable {
  licenseId: string;
  licenseKey: string;
  type: string;
  includes4G: boolean;
  expirationDate: Date;
  assignedCharger?: string;
  ports?: number;
  renewalOptions: Array<{
    currency: string;
    amountCents: number;
    termMonths: number;
  }>;
}

export interface LicenseAuditHistory {
  auditId: string;
  licenseId: string;
  auditEvent: string;
  auditEventTime: string;
  auditEventUpdatedBy: string;
  auditDetails: string;
  companyId: string;
}

export interface LicenseAuditHistoryDifference {
  field: string;
  oldValue: any;
  newValue: any;
}

export interface LicensePostRequest {
  type: string;
  includes4G: boolean;
  duration?: number;
  companyId: string;
  ports?: number;
  orderNote?: string;
  numberOfLicenses: number;
}

export interface LicenseListRequest {
  scope: SCOPE_TYPE;
  limit?: number;
  offset?: number;
  'filter_in[chargerId]'?: string;
  'filter_in[status]'?: string;
  'filter_in[duration]'?: string;
  'filter_in[type]'?: string;
  'filter_in[includes4G]'?: string;
  'filter_like[searchValue]'?: string;
}

export interface LicenseListResponse {
  totalCount: number;
  entities: Array<License>;
}

export interface LicenseSelfServeRenewableListRequest {
  scope: SCOPE_TYPE;
  companyId: string;
  limit?: number;
  offset?: number;
}

export interface LicenseSelfServeRenewableListResponse {
  totalCount: number;
  entities: Array<LicenseSelfServeRenewable>;
}

export enum LICENSE_STATUS {
  ACTIVE = 'ACTIVE',
  ASSIGNED = 'ASSIGNED',
  UNASSIGNED = 'UNASSIGNED',
  EXPIRED = 'EXPIRED',
}

export interface LicensePatchRequestBody {
  licenseKey: string;
  assignedCharger?: string;
  status: LICENSE_STATUS;
}

export interface LicensePatchRequestUrlParam {
  companyId: string;
  scope: SCOPE_TYPE;
  bulkOperation: boolean;
}

export interface LicenseDeleteRequestUrlParams {
  licenseKey: string;
  scope: SCOPE_TYPE;
  companyId: string;
}

export interface AssignLicenseRequestBody {
  assignedCharger: string;
}

export interface AssignLicenseRequestUrlParams {
  licenseKey: string;
  companyId: string;
  scope: SCOPE_TYPE;
}

export interface AssignLicenseRequestToast {
  chargerName: string;
  isChargerDeployed: boolean;
}

export interface AssignLicenseRequest {
  licenseAssignRequestBody: AssignLicenseRequestBody;
  licenseAssignRequestUrlParam: AssignLicenseRequestUrlParams;
  licenseAssignRequestToast: AssignLicenseRequestToast;
}
export interface UnassignLicenseRequestToast {
  chargerName: string;
  licenseKey: string;
}

export interface UnassignLicenseRequestUrlParams {
  licenseKey: string;
  scope: SCOPE_TYPE;
  companyId: string;
}

export interface UnassignLicenseRequest {
  licensePatchRequestUrlParam: UnassignLicenseRequestUrlParams;
  licensePatchRequestToast: UnassignLicenseRequestToast;
}

export interface LicenseEditRequestBody {
  licenseKey: string;
  type: string;
  includes4G: boolean;
  duration?: number;
  ports?: number;
  orderNote?: string;
}

export interface EditLicenseRequest {
  licenseEditRequestBody: LicenseEditRequestBody;
  licenseEditRequestUrlParam: LicensePatchRequestUrlParam;
}

export interface LicenseRenewalRequestUrlParams {
  licenseKey: string;
  scope: SCOPE_TYPE;
  companyId: string;
}

export interface LicenseRenewalRequest {
  licenseRenewalRequestUrlParam: LicenseRenewalRequestUrlParams;
  licenseRenewalRequestBody: {
    duration: number;
    type: string;
  };
}

export interface LicenseSelfServeRenewal {
  licenseId: string;
  duration: number;
}

export interface LicenseSelfServeRenewalRequest {
  params: {
    scope: SCOPE_TYPE;
    companyId: string;
  };
  body: {
    licenses: Array<LicenseSelfServeRenewal>;
  };
}

export interface LicenseSelfServeRenewalResponse {
  renewal: {
    renewalId: string;
    creationDate: Date;
    status: string;
    stripeCheckoutSessionId: string;
    licenses: Array<LicenseSelfServeRenewal>;
    lastUpdateTimestampUtc: Date;
  };
  stripeCheckoutRedirectUrl: string;
}

export interface LicenseAlignmentRequest {
  params: {
    scope: SCOPE_TYPE;
    companyId: string;
  };
  body: {
    ids: Array<string>;
    dayOfMonth: number; // used to request a specific day of the month when querying for an expiry date
    expiryDate?: Date; // only used for setting the expiry date
  };
}

export interface LicenseAlignmentResponse {
  expiryDate: Date; // the date that was computed to align the expiry for the given licenses
  ports: number; // total ports
  portDaysRemaining: number; // normalized number of port days remaining before alignment, weighted so DC counts for more than Level 2
  averageDaysRemaining: number; // the average number of days remaining before the expiry date
  extraTime: number; // The extra days required to round up to the requested day of the month
  extraLicenseUnits: number; // basic-level2-port-days
}
